/**
 * Validity rules for a member's recurring ("additional") direct debit items.
 *
 * These decide whether a run on a given collection date charges an item, so they
 * mirror `DirectDebitRunFileGenerator.IsAdditionalItemCollectableOn` on the API —
 * the grid computes the amount the file is built from, so a rule that differs
 * between the two takes the wrong money.
 *
 *   validFrom  inclusive — an item starting on the collection date is charged.
 *   validTo    exclusive — an item whose end date is on or before the collection
 *              date has stopped, and is not charged. End-dating an item on the run
 *              date is how an operator says "don't take this one"; treating that as
 *              one last collection charges a member for something they were told
 *              had ended.
 *
 * Either bound may be absent, meaning unbounded. The API writes .NET's
 * `DateTime.MinValue` (year 1) for "no end date", so a year before 1900 counts as
 * absent too.
 */

import { Dayjs } from 'dayjs';

/** A validity bound, as it can arrive: a Date (see DateReviver), an ISO string, or nothing. */
export type ValidityDate = Date | string | null | undefined;

/** A window either bound of which may be open. */
export interface ValidityWindow {
    validFrom?: ValidityDate;
    validTo?: ValidityDate;
}

/** A calendar date with no time and no zone — the only thing these bounds actually mean. */
interface CalendarDate {
    year: number;
    month: number;
    day: number;
}

const DATE_ONLY_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Read the calendar date a bound denotes.
 *
 * `valid_from` / `valid_to` are Postgres `date` columns, so neither end of the wire
 * carries a time or a zone — but both ends are happy to invent one:
 *
 *  - a bare `YYYY-MM-DD` string goes through `new Date()` as midnight **UTC**, which
 *    local getters then read as the previous day anywhere west of Greenwich; while
 *  - the API's own `"YYYY-MM-DDT00:00:00"` (DateTime, unspecified kind) parses as
 *    midnight **local**, where UTC getters are the ones that name the wrong day.
 *
 * So a string is read as the calendar fields it literally spells, whatever suffix
 * follows, and a `Date` — which the API client's DateReviver has already built at
 * local midnight from that same serialization — is read with local getters. Neither
 * path passes through a timezone conversion. See `utils/DateOnly.tsx` for the same
 * hazard on the way out.
 */
const toCalendarDate = (value: ValidityDate): CalendarDate | null => {
    if (value === null || value === undefined || value === '') return null;

    if (typeof value === 'string') {
        const match = DATE_ONLY_PREFIX.exec(value);
        if (match) return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return null;
        return { year: parsed.getFullYear(), month: parsed.getMonth() + 1, day: parsed.getDate() };
    }

    if (Number.isNaN(value.getTime())) return null;
    return { year: value.getFullYear(), month: value.getMonth() + 1, day: value.getDate() };
};

/** Calendar dates as a comparable ordinal. Never a timestamp — see `toCalendarDate`. */
const ordinal = ({ year, month, day }: CalendarDate): number => year * 10000 + month * 100 + day;

/**
 * Whether a bound means "no bound". Covers a missing value and the .NET
 * `DateTime.MinValue`/`DateOnly.MinValue` sentinel (0001-01-01) the API sends for
 * an open-ended item.
 */
export const isOpenEndedDate = (value: ValidityDate): boolean => {
    const date = toCalendarDate(value);
    return date === null || date.year < 1900;
};

/** Whether a run collecting on `collectionDate` should charge this item. */
export const isItemCollectableOn = (item: ValidityWindow, collectionDate: Date | Dayjs): boolean => {
    const collection = toCalendarDate(collectionDate instanceof Date ? collectionDate : collectionDate.toDate());
    if (collection === null) return false;
    const collectionOrdinal = ordinal(collection);

    if (!isOpenEndedDate(item.validFrom)) {
        const from = toCalendarDate(item.validFrom);
        if (from !== null && ordinal(from) > collectionOrdinal) return false;
    }

    if (!isOpenEndedDate(item.validTo)) {
        const to = toCalendarDate(item.validTo);
        if (to !== null && ordinal(to) <= collectionOrdinal) return false;
    }

    return true;
};

/** The subset of `items` a run on `collectionDate` charges. */
export const getCollectableItems = <T extends ValidityWindow>(
    items: T[] | null | undefined,
    collectionDate: Date | Dayjs
): T[] => {
    if (!items || !Array.isArray(items)) return [];
    return items.filter((item) => isItemCollectableOn(item, collectionDate));
};
